import {
    ViewViewModel,
    ComponentManager,
    NavigationHelper,
} from 'radpresentation.htmlclient.defaultviewextensions';

import { IAgeSeries } from '@/Components/AgeChart/ONVAgeBarChartElement';
import { fetchAgeDistribution } from '@/Components/AgeChart/ageDistributionService';
// Registriert das KO-Binding "onvAgeBarChart" (geteilt mit der Statistik-Seite).
import '@/Components/AgeChart/onvAgeBarChartBinding';
import { fetchStartpageHints } from '@/Services/NewcomerStatus/startpageHintsService';
import {
    NEWCOMER_PD_TYPE,
    FILTER_KEYWORD_UNANSWERED,
    FILTER_KEYWORD_WITHOUT_COURSE,
} from '@/Services/NewcomerStatus/startpageNewcomerFilters';


export default class ONVStartpageViewModel extends ViewViewModel
{
    // Reaktive Daten für das Web Component (via KO-Binding übergeben).
    public readonly isLoading:    KnockoutObservable<boolean>;
    public readonly errorMessage: KnockoutObservable<string | null>;
    public readonly generatedAt:  KnockoutObservable<string | null>;
    public readonly rawSeries:    KnockoutObservableArray<IAgeSeries>;

    // Zeithorizonte: Zustand liegt hier, da eine Änderung eine neue Abfrage auslöst.
    public readonly selectedHorizons: KnockoutObservableArray<number>;

    // Hinweis-Kennzahlen des aktuellen Mandanten (vom Backend geladen).
    public readonly unansweredMessages:     KnockoutObservable<number>;
    public readonly newcomersWithoutCourse: KnockoutObservable<number>;
    public readonly hintsLoaded:            KnockoutObservable<boolean>;

    constructor(params: any)
    {
        super(params);

        this.selectedHorizons = ko.observableArray<number>([3, 6]);
        this.isLoading        = this.observableOrDefault<boolean>(true);
        this.errorMessage     = this.observableOrDefault<string | null>(null);
        this.generatedAt      = this.observableOrDefault<string | null>(null);
        this.rawSeries        = ko.observableArray<IAgeSeries>([]);

        this.unansweredMessages     = ko.observable<number>(0);
        this.newcomersWithoutCourse = ko.observable<number>(0);
        this.hintsLoaded            = ko.observable<boolean>(false);

        // Änderung der Zeithorizonte (ausgelöst durch horizons-change-Event) → neue Abfrage.
        this.selectedHorizons.subscribe(() => this.reload());

        this.reload();
        this.loadHints();
    }
    
    /**
     * Laedt die Hinweis-Kennzahlen (unbeantwortete Newcomer, aktive Newcomer ohne
     * Kurszuordnung) fuer den aktuellen Mandanten. Bei einem Fehler bleiben die Werte
     * auf 0; hintsLoaded steuert die Anzeige im Template.
     */
    private loadHints(): void
    {
        fetchStartpageHints().then(
            (hints) => {
                if (this.isDisposed) return;
                this.unansweredMessages(hints?.unansweredNewcomers ?? 0);
                this.newcomersWithoutCourse(hints?.newcomersWithoutCourse ?? 0);
                this.hintsLoaded(true);
            },
            () => {
                if (this.isDisposed) return;
                this.unansweredMessages(0);
                this.newcomersWithoutCourse(0);
                this.hintsLoaded(true);
            },
        );
    }
    
    /**
     * Oeffnet die Newcomer-ListView, eingeschraenkt auf die unbeantworteten Newcomer.
     * Das Filter-Keyword wird serverseitig (PDIteratorEventHandler) durch die passenden
     * Newcomer-PDO-IDs ersetzt.
     */
    public openUnansweredNewcomers(): void
    {
        NavigationHelper.openListViewByType(NEWCOMER_PD_TYPE, { filter: FILTER_KEYWORD_UNANSWERED });
    }

    /**
     * Oeffnet die Newcomer-ListView, eingeschraenkt auf aktive Newcomer ohne Kurszuordnung.
     */
    public openNewcomersWithoutCourse(): void
    {
        NavigationHelper.openListViewByType(NEWCOMER_PD_TYPE, { filter: FILTER_KEYWORD_WITHOUT_COURSE });
    }
    
    /** Wird vom KO-Binding aufgerufen, sobald der Nutzer Zeithorizonte im Chart ändert. */
    public onHorizonsChange(horizons: number[]): void
    {
        this.selectedHorizons(horizons.slice().sort((a, b) => a - b));
    }
    
    private reload(): void
    {
        const horizons = this.selectedHorizons().slice().sort((a, b) => a - b);
        if (horizons.length === 0) return;

        this.isLoading(true);
        this.errorMessage(null);

        // Ohne Landesverband-Id: folgt dem Principal-Filter (eigener Landesverband).
        fetchAgeDistribution(horizons).then(
            (parsed) => {
                if (this.isDisposed) return;
                this.rawSeries(parsed?.series ?? []);
                this.generatedAt(parsed?.generatedAt ?? null);
                this.isLoading(false);
            },
            () => {
                if (this.isDisposed) return;
                this.rawSeries([]);
                this.errorMessage('Die Altersverteilung konnte nicht geladen werden.');
                this.isLoading(false);
            },
        );
    }
}

ComponentManager.setViewModel('ONVStartpageViewModel', ONVStartpageViewModel, 'ONVHTMLViewExtension');
ComponentManager.setViewModel('StartpageViewModel', ONVStartpageViewModel, 'ONVHTMLViewExtension');